import { ColorToken, Typography } from "@flash-tecnologia/hros-web-ui-v2";
import React from "react";
import PillSelect from "@flash-tecnologia/flamingo-ui-pill-select";
import { Number } from "./components/Number/number";
import { useTheme } from "styled-components";
import * as SC from "./stepper.styles";

export enum StepMoment {
  Attention = "attention",
  Active = "active",
  Completed = "completed",
  Default = "default",
}

export interface StepperStep {
  id: string;
  label: string;
  helperText?: string;
  state?: StepMoment.Attention | StepMoment.Completed;
}

export interface StepperProps {
  activeStepId: string;
  meta?: string;
  steps: StepperStep[];
  onActiveStepChange?: (stepId: string) => void;
}

export const Stepper = ({
  activeStepId,
  meta,
  onActiveStepChange,
  steps,
}: StepperProps) => {
  const theme = useTheme();

  if (!steps?.length) {
    return null;
  }
  const activeStep = steps.find(({ id }) => id === activeStepId);

  return (
    <PillSelect.Root size="small" variant="secondary" variantType="brand">
      <SC.StepperTrigger>
        <PillSelect.MainIcon name="IconProgress" />
        {activeStep && <PillSelect.Label>{activeStep.label}</PillSelect.Label>}
        <PillSelect.Meta>{meta}</PillSelect.Meta>
      </SC.StepperTrigger>
      <PillSelect.Dropdown>
        <PillSelect.DropdownContent>
          <SC.VerticalStepsGrid>
            {steps.map(({ id, label, helperText, state }, index) => {
              let stepMoment: StepMoment = StepMoment.Default;

              if (id === activeStepId) {
                stepMoment = StepMoment.Active;
              } else if (state === StepMoment.Attention) {
                stepMoment = StepMoment.Attention;
              } else if (state === StepMoment.Completed) {
                stepMoment = StepMoment.Completed;
              }

              const labelColor = {
                [StepMoment.Active]: "secondary.40",
                [StepMoment.Attention]: "neutral.50",
                [StepMoment.Completed]: "neutral.50",
                [StepMoment.Default]: "neutral.60",
              }[stepMoment] as ColorToken;

              return (
                <React.Fragment key={id}>
                  <SC.StepState
                    isFirst={index === 0}
                    isLast={index === steps.length - 1}
                  >
                    {
                      {
                        [StepMoment.Active]: (
                          <Number
                            variant="active"
                            size={24}
                            onClick={() => onActiveStepChange?.(id)}
                          >
                            {`${index + 1}`}
                          </Number>
                        ),
                        [StepMoment.Attention]: (
                          <SC.ShapeIconHoverable
                            size={24}
                            variant="error"
                            color={theme.colors.feedback.error[10]}
                            name="IconExclamationMark"
                            onClick={() => onActiveStepChange?.(id)}
                          />
                        ),
                        [StepMoment.Completed]: (
                          <SC.ShapeIconHoverable
                            size={24}
                            variant="success"
                            color={theme.colors.feedback.success[10]}
                            name="IconCheck"
                            onClick={() => onActiveStepChange?.(id)}
                          />
                        ),
                        [StepMoment.Default]: (
                          <Number
                            size={24}
                            variant="default"
                            onClick={() => onActiveStepChange?.(id)}
                          >{`${index + 1}`}</Number>
                        ),
                      }[stepMoment]
                    }
                  </SC.StepState>
                  <SC.StepContent>
                    <Typography variant="body4" color={labelColor} weight={600}>
                      {label}
                    </Typography>
                    {helperText && (
                      <Typography variant="caption" color="neutral.60">
                        {helperText}
                      </Typography>
                    )}
                  </SC.StepContent>
                </React.Fragment>
              );
            })}
          </SC.VerticalStepsGrid>
        </PillSelect.DropdownContent>
      </PillSelect.Dropdown>
    </PillSelect.Root>
  );
};
